import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { StandardTitle } from './StandardTitle';

const styles = StyleSheet.create({
  container: {
    marginBottom: 15,
  },
  sectionHeader: {
    fontSize: 16,
    color: '#004899',
    fontWeight: 'bold',
    marginBottom: 10,
    backgroundColor: '#f8f9fa',
    padding: 8,
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: '#004899',
    textAlign: 'center',
  },
  modeHeader: {
    fontSize: 14,
    color: '#004899',
    fontWeight: 'bold',
    marginTop: 15,
    marginBottom: 10,
    backgroundColor: '#f0f5ff',
    padding: 6,
    borderRadius: 4,
  },
  noDataText: {
    fontSize: 10,
    color: '#666',
    fontStyle: 'italic',
    textAlign: 'center',
    marginBottom: 10,
    paddingTop: 5,
    paddingBottom: 5,
  },
  layoutContainer: {
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 6,
    overflow: 'hidden',
  },
  gridTable: {
    width: '100%',
  },
  gridHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#f0f5ff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  gridRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  gridLabelCell: {
    width: '33.33%',
    padding: 6,
    backgroundColor: '#f0f5ff',
    fontSize: 9,
    fontWeight: 'bold',
    borderRightWidth: 1,
    borderRightColor: '#e0e0e0',
  },
  gridCell: {
    width: '33.33%',
    padding: 6,
    fontSize: 9,
    borderRightWidth: 1,
    borderRightColor: '#e0e0e0',
  },
  lastCell: {
    borderRightWidth: 0,
  },
  detailsContainer: {
    padding: 10,
  },
  detailsGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  detailsRow: {
    width: '50%',
    marginBottom: 10,
    paddingRight: 5,
  },
  detailsLabel: {
    fontSize: 9,
    color: '#666',
    marginBottom: 3,
    fontWeight: 'bold',
  },
  detailsValue: {
    fontSize: 9,
    color: '#333',
    backgroundColor: '#f7f7f7',
    padding: 5,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
});

interface ShippingMode {
  id?: number;
  mode: number | string;
  transitMode: string;
  postShipTruckTestLayouts?: Array<any>;
  startCity?: string;
  endCity?: string;
  startStateName?: string;
  endStateName?: string;
  distanceTravelled?: string;
}

interface TruckLayoutSectionProps {
  shippingModes: ShippingMode[];
}

export const TruckLayoutSection: React.FC<TruckLayoutSectionProps> = ({ shippingModes }) => {
  // Filter shipping modes that should display truck layout (air ride or leaf spring)
  const truckModes = shippingModes.filter(mode =>
    mode.transitMode === 'Truck Air ride' ||
    mode.transitMode === 'Truck leaf spring'
  );

  if (truckModes.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.sectionHeader}>Truck Layout</Text>
        <Text style={styles.noDataText}>No truck shipping modes found for this project.</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <StandardTitle title="Truck Layout" />

      {truckModes.map((mode, index) => {
        const truckLayout = mode.postShipTruckTestLayouts && mode.postShipTruckTestLayouts.length > 0
          ? mode.postShipTruckTestLayouts[0]
          : null;

        return (
          <View key={`truck-layout-${index}`}>
            <Text style={styles.modeHeader}>
              Shipping Mode {mode.mode} - {mode.transitMode}
            </Text>

            {truckLayout ? (
              <View style={styles.layoutContainer}>
                {/* Load Pickup & Delivery Section */}
                <View style={styles.gridTable}>
                  <View style={styles.gridHeaderRow}>
                    <Text style={[styles.gridLabelCell, { width: '34%' }]}></Text>
                    <Text style={[styles.gridCell, { width: '33%', backgroundColor: '#f0f5ff', fontWeight: 'bold' }]}>
                      Load Pickup
                    </Text>
                    <Text style={[styles.gridCell, { width: '33%', backgroundColor: '#f0f5ff', fontWeight: 'bold', ...styles.lastCell }]}>
                      Load Delivery
                    </Text>
                  </View>

                  {/* Name */}
                  <View style={styles.gridRow}>
                    <Text style={[styles.gridLabelCell, { width: '34%' }]}>
                      Name
                    </Text>
                    <Text style={[styles.gridCell, { width: '33%' }]}>
                      {truckLayout.pickupName || 'N/A'}
                    </Text>
                    <Text style={[styles.gridCell, { width: '33%', ...styles.lastCell }]}>
                      {truckLayout.deliveryName || 'N/A'}
                    </Text>
                  </View>

                  {/* Telephone */}
                  <View style={styles.gridRow}>
                    <Text style={[styles.gridLabelCell, { width: '34%' }]}>
                      Telephone
                    </Text>
                    <Text style={[styles.gridCell, { width: '33%' }]}>
                      {truckLayout.pickupPhone || 'N/A'}
                    </Text>
                    <Text style={[styles.gridCell, { width: '33%', ...styles.lastCell }]}>
                      {truckLayout.deliveryPhone || 'N/A'}
                    </Text>
                  </View>

                  {/* Email */}
                  <View style={styles.gridRow}>
                    <Text style={[styles.gridLabelCell, { width: '34%' }]}>
                      Email
                    </Text>
                    <Text style={[styles.gridCell, { width: '33%' }]}>
                      {truckLayout.pickupEmail || 'N/A'}
                    </Text>
                    <Text style={[styles.gridCell, { width: '33%', ...styles.lastCell }]}>
                      {truckLayout.deliveryEmail || 'N/A'}
                    </Text>
                  </View>
                </View>

                {/* Truck Details in a grid */}
                <View style={styles.detailsContainer}>
                  <View style={styles.detailsGrid}>
                    {[
                      { key: 'startingMileage', label: 'Starting Mileage' },
                      { key: 'endingMileage', label: 'Ending Mileage' },
                      { key: 'totalMilesDriven', label: 'Total Miles Driven' },
                      { key: 'suspensionModel', label: 'Suspension Model' },
                      { key: 'year', label: 'Year' },
                      { key: 'truckCondition', label: 'Truck Condition' },
                      { key: 'airbags', label: 'Airbags' },
                      { key: 'lockBarsStraps', label: 'Lock Bars/Straps' },
                    ].map(({ key, label }) => (
                      <View style={styles.detailsRow} key={key}>
                        <Text style={styles.detailsLabel}>{label}</Text>
                        <Text style={styles.detailsValue}>
                          {truckLayout[key] || 'N/A'}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>

                {/* Route Summary */}
                <View style={[styles.detailsContainer, { backgroundColor: '#f9f9f9', borderTopWidth: 1, borderTopColor: '#e0e0e0' }]}>
                  <Text style={[styles.detailsLabel, { marginBottom: 8 }]}>Route Summary</Text>
                  <View style={styles.detailsGrid}>
                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>From</Text>
                      <Text style={styles.detailsValue}>
                        {mode.startCity}, {mode.startStateName}
                      </Text>
                    </View>
                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>To</Text>
                      <Text style={styles.detailsValue}>
                        {mode.endCity}, {mode.endStateName}
                      </Text>
                    </View>
                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Distance</Text>
                      <Text style={styles.detailsValue}>
                        {mode.distanceTravelled} miles
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
            ) : (
              <View style={styles.layoutContainer}>
                <Text style={styles.noDataText}>
                  No truck layout data available for Shipping Mode {mode.mode}.
                </Text>
              </View>
            )}
          </View>
        );
      })}

      {/* If there are other shipping modes, show a message */}
      {shippingModes.length > truckModes.length && (
        <View style={{ marginTop: 15 }}>
          <Text style={styles.noDataText}>
            Note: {shippingModes.length - truckModes.length} shipping {shippingModes.length - truckModes.length === 1 ? 'mode is' : 'modes are'} not displayed as {shippingModes.length - truckModes.length === 1 ? 'it is' : 'they are'} not truck shipping {shippingModes.length - truckModes.length === 1 ? 'mode' : 'modes'}.
          </Text>
        </View>
      )}
    </View>
  );
}; 